import { Component, OnInit } from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
} from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { switchMap, filter, distinctUntilChanged } from "rxjs/operators";
import { AuthService } from "./core/services/auth.service";
import { CompanyService } from "./core/services/company.service";
import { LoadingService } from "./core/services/loading.service";
import { SentryUserService } from "./shared/services/sentry-user.service";

/**
 * App component.
 */
@Component({
  selector: "app-hyper",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  constructor(
    public loading: LoadingService,
    private company: CompanyService,
    private router: Router,
    private modalService: NgbModal,
    private authService: AuthService,
    private sentryUserService: SentryUserService
  ) {
    /**
     * Start angular-oidc login process.
     *
     * If the user is already logged in, redirect to overview or dashboard page based on status.
     * If not, redirect to the login form.
     */
    this.authService.runInitialLoginSequence();

    /**
     * If user is authententicated, get company information.
     */
    this.authService.isAuthenticated$
      .pipe(
        filter((authenticated) => authenticated),
        distinctUntilChanged(),
        switchMap(() => this.company.getCompanyInfo()),
      )
      .subscribe(() => {
        /**
         * After company information is loaded, show app content.
         */
        this.loading.setAppLoading(false);
      });

    /**
     * Listen for router changes.
     * Show loading view when route change starts
     * and hide it when route change ends.
     *
     * When route changes, dismiss any open modal.
     * Track navigation in Sentry breadcrumbs for error debugging.
     */
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.loading.setRouterLoading(false);
        this.modalService.dismissAll();

        // Track navigation in Sentry breadcrumbs for error debugging
        this.sentryUserService.addBreadcrumb(
          'navigation',
          `Navigated to ${this.getReadableRouteName(e.urlAfterRedirects)}`,
          'info',
          {
            url: e.urlAfterRedirects,
            id: e.id,
          }
        );
      } else if (e instanceof NavigationCancel) {
        this.loading.setRouterLoading(false);
        this.modalService.dismissAll();

        // Track navigation cancellations for debugging
        this.sentryUserService.addBreadcrumb(
          'navigation',
          `Navigation cancelled: ${this.getReadableRouteName(e.url)}`,
          'warning',
          {
            url: e.url,
            reason: e.reason,
          }
        );
      } else if (e instanceof NavigationStart) {
        this.loading.setRouterLoading(true);
      }
    });
  }

  ngOnInit() {}

  /**
   * Extract a human-readable route name from the URL for breadcrumb tracking
   * @param url - The router URL
   * @returns A formatted, readable route name
   * @private
   */
  private getReadableRouteName(url: string): string {
    // Remove query parameters and fragments
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Map of common routes to readable names
    const routeMap: Record<string, string> = {
      '/': 'Home',
      '/auth': 'Auth',
      '/auth/login': 'Login',
      '/dashboard': 'Dashboard',
      '/jobs': 'Jobs',
      '/profile': 'Profile',
      '/settings': 'Settings',
      '/documents': 'Documents',
      '/forms': 'Forms',
    };

    // Check for exact matches first
    if (routeMap[cleanUrl]) {
      return routeMap[cleanUrl];
    }

    // Handle dynamic routes (e.g., /jobs/123 -> "Jobs > Detail")
    const segments = cleanUrl.split('/').filter(Boolean);
    if (segments.length === 0) return 'Home';

    // Convert URL segments to readable format
    const readableSegments = segments
      .map((segment) => {
        // Skip numeric IDs and UUIDs
        if (/^\d+$/.test(segment) || /^[0-9a-f-]{36}$/i.test(segment)) {
          return 'Detail';
        }

        // Convert kebab-case to Title Case
        return segment
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      })
      .filter((segment, index, array) => {
        // Remove duplicate "Detail" segments
        return segment !== 'Detail' || index === array.lastIndexOf('Detail');
      });

    return readableSegments.join(' > ');
  }
}
